// English strings for the settings stream: installation settings in the platform console (owner
// instruction PD-710; PD-665 – PD-689). Labels and help per registry key: set.k.<key>.label/.help.
// Arabic: ar-settings.ts.
export const enSettings = {
  // ------------------------------------------------------------ page
  'set.nav': 'Installation settings',
  'set.title': 'Installation settings',
  'set.intro': 'Limits, timeouts, schedules and provider switches for this installation. Changes apply without a restart: at once in this process, within a second everywhere else. Every change is recorded in the platform audit trail.',
  'set.loadingFailed': 'The installation settings could not be loaded.',
  'set.version': 'Settings version {version}',
  'set.filter': 'Show',
  'set.filter.all': 'All settings',
  'set.filter.changed': 'Changed from the default',
  // ------------------------------------------------------------ groups
  'set.g.security': 'Security and sign-in',
  'set.g.email': 'Email and messages',
  'set.g.integrations': 'Integrations',
  'set.g.malware': 'Malware scanning',
  'set.g.ai': 'AI add-ons',
  'set.g.worker': 'Worker and background jobs',
  'set.g.rateLimits': 'Rate limits',
  'set.g.exports': 'Exports, imports and reports',
  'set.g.devices': 'Devices',
  // ------------------------------------------------------------ one setting
  'set.default': 'Default: {value}',
  'set.isDefault': 'Default',
  'set.changed': 'Changed',
  'set.source.console': 'Set in the console',
  'set.source.environment': 'Imported once from the environment',
  'set.source.default': 'Built-in default',
  'set.updated': 'Last changed {at} by {by}',
  'set.updatedSystem': 'Last changed {at} (environment import)',
  'set.updatedAt': 'Last changed {at}',
  'set.replaces': 'Replaces the environment variable {name}',
  'set.usedBy.api': 'Used by the API',
  'set.usedBy.worker': 'Used by the worker',
  'set.usedBy.both': 'Used by the API and the worker',
  'set.sensitive': 'Sensitive: needs a reason and a confirmation',
  'set.save': 'Save',
  'set.saved': 'Saved. The new value is in effect.',
  'set.reset': 'Reset to default',
  'set.resetDone': 'Reset. The default is in effect.',
  'set.storedProblem': 'The stored value is not in effect here ({problem}); the default applies until it is changed.',
  'set.on': 'On',
  'set.off': 'Off',
  'set.none': 'Not set',
  'set.secret.set': 'A key is stored (it is never shown).',
  'set.secret.notSet': 'No key is stored.',
  'set.secret.new': 'New value',
  'set.secret.replace': 'Store key',
  'set.secret.clear': 'Remove stored key',
  'set.secret.help': 'Write-only: the value is sealed with the identity key ring and cannot be read back, even by system administrators.',
  'set.conflict': 'Someone else changed this setting. The page now shows the current value; check it and try again.',
  // ------------------------------------------------------------ confirmation dialog
  'set.confirm.title': 'Change {label}?',
  'set.confirm.body': 'This setting affects security or what the installation can reach. The change applies at once to every organisation and is recorded with your reason.',
  'set.confirm.from': 'Current: {value}',
  'set.confirm.to': 'New: {value}',
  'set.confirm.reason': 'Reason',
  'set.confirm.check': 'I understand the consequence of this change.',
  'set.confirm.apply': 'Apply change',
  // ------------------------------------------------------------ units
  'set.unit.ms': 'milliseconds',
  'set.unit.seconds': 'seconds',
  'set.unit.hours': 'hours',
  'set.unit.days': 'days',
  'set.unit.bytes': 'bytes',
  'set.unit.kib': 'KiB',
  'set.unit.perMinute': 'per minute',
  'set.range': 'Allowed: {min} – {max}',
  'set.productionMin': 'At least {min} in production',
  // ------------------------------------------------------------ option labels
  'set.o.none': 'None',
  'set.o.local': 'Local development outbox',
  'set.o.simulator': 'Simulator (development only)',
  'set.o.clamav': 'ClamAV',
  'set.o.low': 'Low',
  'set.o.medium': 'Medium',
  'set.o.high': 'High',
  'set.o.local-rules': 'Local rules (offline)',
  'set.o.anthropic': 'Anthropic (external)',
  // ------------------------------------------------------------ problems and validation
  'set.p.productionMinimum': 'Below the production minimum of {min}.',
  'set.p.simulatorProduction': 'Simulators are not allowed in production.',
  'set.p.localOutboxProduction': 'The local development outbox is not allowed in production.',
  'set.p.clamavNotWired': 'ClamAV is not wired for this deployment (CLAMAV_HOST is not set).',
  'set.p.wrongKeyRing': 'The stored secret was sealed with another key ring.',
  'set.p.keyRingMissing': 'The key ring for this secret is not available.',
  'set.v.modelId': 'Enter a model id such as claude-opus-5-5.',
  'set.v.secretLength': 'Enter between 10 and 500 characters.',
  'set.v.reasonRequired': 'Give a reason for this change.',
  'set.v.confirmRequired': 'Confirm that you understand the consequence.',
  'set.v.hostnameGuard': 'This host name is not allowed for this deployment profile.',
  // ------------------------------------------------------------ environment facts (read only)
  'set.env.title': 'From the environment',
  'set.env.intro': 'These stay in the deployment environment (database connections, key rings, network and service wiring). They are shown read only so you can see what still comes from there.',
  'set.env.profile': 'Deployment profile',
  'set.env.nodeEnv': 'Process mode',
  'set.env.version': 'Version',
  'set.env.consoleHosts': 'Console host names',
  'set.env.scheme': 'Public scheme and port',
  'set.env.defaultPort': 'default port',
  'set.env.origins': 'Accepted origins (derived from organisation domains)',
  'set.env.excluded': 'Left out by the hostname rules: {hostname} — {reason}',
  'set.env.clamav': 'ClamAV service wired',
  'set.env.keyRing': 'Identity key ring for sealed settings',
  'set.env.yes': 'Yes',
  'set.env.no': 'No',
  'set.env.ignored': 'Moved variables still in the environment (ignored)',
  'set.env.retired': 'Retired variables still in the environment (never read)',
  'set.env.noneListed': 'None',
  // ------------------------------------------------------------ settings (label / help)
  'set.k.password.hashMemoryKiB.label': 'Password hashing memory',
  'set.k.password.hashMemoryKiB.help': 'argon2id memory cost for new password hashes. Existing hashes keep their parameters and are upgraded at the next sign-in.',
  'set.k.password.hashPasses.label': 'Password hashing passes',
  'set.k.password.hashPasses.help': 'argon2id time cost (iterations) for new password hashes.',
  'set.k.password.hashParallelism.label': 'Password hashing parallelism',
  'set.k.password.hashParallelism.help': 'argon2id lanes for new password hashes.',
  'set.k.verification.simulators.label': 'Identity verification simulators',
  'set.k.verification.simulators.help': 'Local simulators for UAE PASS, the Emirates ID reader and document scanning. Development only; never in production.',
  'set.k.mail.smtpTimeoutMs.label': 'SMTP timeout',
  'set.k.mail.smtpTimeoutMs.help': 'Connection, greeting and per-command limit. A timeout after the message was handed over is recorded as unknown, never as sent.',
  'set.k.mail.organisationPrivateNetworks.label': 'Organisation SMTP servers on private networks',
  'set.k.mail.organisationPrivateNetworks.help': 'Allow organisations that send as themselves to use an SMTP server on a private network address. The installation default may always use one.',
  'set.k.sms.provider.label': 'SMS provider',
  'set.k.sms.provider.help': 'No SMS provider is chosen yet. "None" records each SMS as blocked; the local outbox writes files for development.',
  'set.k.whatsapp.provider.label': 'WhatsApp provider',
  'set.k.whatsapp.provider.help': 'No WhatsApp provider is chosen yet. "None" records each message as blocked; the local outbox writes files for development.',
  'set.k.api.tokenDays.label': 'API client token lifetime',
  'set.k.api.tokenDays.help': 'How long a newly issued API client token stays valid.',
  'set.k.webhook.privateNetworks.label': 'Webhooks to private networks',
  'set.k.webhook.privateNetworks.help': 'Allow webhook subscriptions to deliver to private network addresses.',
  'set.k.webhook.timeoutMs.label': 'Webhook timeout',
  'set.k.webhook.timeoutMs.help': 'A delivery that is not answered in time is retried; its outcome is unknown, never success.',
  'set.k.accessAdapter.privateNetworks.label': 'Access-control adapters on private networks',
  'set.k.accessAdapter.privateNetworks.help': 'Allow the generic REST access-control adapter to reach controllers on private network addresses.',
  'set.k.calendar.providerMode.label': 'Calendar providers',
  'set.k.calendar.providerMode.help': 'Only provider simulators exist so far. "None" keeps calendar connections as configuration only.',
  'set.k.calendar.providerTimeoutMs.label': 'Calendar provider timeout',
  'set.k.calendar.providerTimeoutMs.help': 'A provider call without an answer in time has an unknown outcome.',
  'set.k.calendar.syncIntervalSeconds.label': 'Calendar synchronisation interval',
  'set.k.calendar.syncIntervalSeconds.help': 'How often subscriptions are renewed and room calendars synchronised. 0 switches the scheduler off.',
  'set.k.directory.syncIntervalSeconds.label': 'Directory synchronisation interval',
  'set.k.directory.syncIntervalSeconds.help': 'How often due directory synchronisations are started. 0 switches the scheduler off.',
  'set.k.directory.testSampleSize.label': 'Directory test sample',
  'set.k.directory.testSampleSize.help': 'Entries a directory "test connection" reads (only counts are shown).',
  'set.k.malware.scanner.label': 'Upload malware scanner',
  'set.k.malware.scanner.help': 'With "None", uploads are not scanned and visitor or contractor files are accepted only as an original seen in person. ClamAV needs the scanner service wired in the deployment.',
  'set.k.clamav.connectTimeoutMs.label': 'ClamAV connection timeout',
  'set.k.clamav.connectTimeoutMs.help': 'How long to wait for the scanner to accept a connection.',
  'set.k.clamav.scanTimeoutMs.label': 'ClamAV scan timeout',
  'set.k.clamav.scanTimeoutMs.help': 'A scan not answered in time is "could not be checked", never clean.',
  'set.k.clamav.maxStreamBytes.label': 'ClamAV largest file',
  'set.k.clamav.maxStreamBytes.help': 'Keep equal to the scanner\'s StreamMaxLength; never below the largest upload (10 MiB).',
  'set.k.clamav.chunkBytes.label': 'ClamAV chunk size',
  'set.k.clamav.chunkBytes.help': 'Bytes sent to the scanner per chunk.',
  'set.k.clamav.signatureMaxAgeDays.label': 'Signature age warning',
  'set.k.clamav.signatureMaxAgeDays.help': 'Operations health warns when the signature database is older than this.',
  'set.k.ai.providers.label': 'AI adapters offered',
  'set.k.ai.providers.help': 'Adapters organisations may choose (each organisation still decides whether to use AI). "None" is always available. External adapters need a data-processing agreement first.',
  'set.k.ai.providerTimeoutMs.label': 'AI provider timeout',
  'set.k.ai.providerTimeoutMs.help': 'A provider call without an answer in time is recorded as unavailable, never as an answer.',
  'set.k.ai.anthropicApiKey.label': 'Anthropic key for the installation',
  'set.k.ai.anthropicApiKey.help': 'Optional. Without it each organisation\'s AI configuration administrator stores its own sealed key.',
  'set.k.ai.anthropicModel.label': 'Anthropic model',
  'set.k.ai.anthropicModel.help': 'Model id used for external AI requests.',
  'set.k.ai.anthropicEffort.label': 'Anthropic effort',
  'set.k.ai.anthropicEffort.help': 'Reasoning effort requested from the model.',
  'set.k.ai.anthropicMaxTokens.label': 'Anthropic answer limit',
  'set.k.ai.anthropicMaxTokens.help': 'Maximum tokens in one answer.',
  'set.k.ai.anthropicMaxRetries.label': 'Anthropic retries',
  'set.k.ai.anthropicMaxRetries.help': 'Retries for retryable errors only (rate limit, server errors, connection), within the timeout.',
  'set.k.ai.anthropicFallbacks.label': 'Anthropic refusal fallback',
  'set.k.ai.anthropicFallbacks.help': 'Let the provider retry a refused request with its server-side fallback (same provider).',
  'set.k.ai.anthropicInferenceGeo.label': 'Anthropic inference region',
  'set.k.ai.anthropicInferenceGeo.help': 'Optional region hint sent with external requests. Empty = provider default. No residency claim is made from it.',
  'set.k.worker.pollMs.label': 'Worker poll interval',
  'set.k.worker.pollMs.help': 'How long the worker waits when the outbox is empty.',
  'set.k.worker.batchSize.label': 'Worker batch size',
  'set.k.worker.batchSize.help': 'Outbox events claimed per batch.',
  'set.k.worker.leaseMs.label': 'Worker lease',
  'set.k.worker.leaseMs.help': 'How long a claimed event stays reserved before another worker may take it.',
  'set.k.worker.maxAttempts.label': 'Worker attempts',
  'set.k.worker.maxAttempts.help': 'Attempts before an event is dead-lettered for an operator.',
  'set.k.worker.sweepMs.label': 'Worker sweep interval',
  'set.k.worker.sweepMs.help': 'How often the periodic sweeps run (visit expiry, overstays, reminders, approvals, reports).',
  'set.k.worker.accessEventPollMs.label': 'Access-event poll interval',
  'set.k.worker.accessEventPollMs.help': 'Minimum time between access-event polls of one connector. 0 switches polling off.',
  'set.k.idempotency.retentionHours.label': 'Idempotency record retention',
  'set.k.idempotency.retentionHours.help': 'How long a repeated command with the same key returns the first result.',
  'set.k.privacy.plannerIntervalSeconds.label': 'Retention planning interval',
  'set.k.privacy.plannerIntervalSeconds.help': 'How often due purges are planned. Planning never applies a purge. 0 switches it off.',
  'set.k.api.rateLimitPerMinute.label': 'Default API client limit',
  'set.k.api.rateLimitPerMinute.help': 'Requests per minute a newly created API client gets. Existing clients keep their own limit.',
  'set.k.reception.lookupPerMinute.label': 'Reception lookups',
  'set.k.reception.lookupPerMinute.help': 'Reception lookups per user and minute.',
  'set.k.reception.sensitiveLookupPerMinute.label': 'Sensitive reception lookups',
  'set.k.reception.sensitiveLookupPerMinute.help': 'Plate and identity-number lookups per user and minute.',
  'set.k.exports.ttlHours.label': 'Export download window',
  'set.k.exports.ttlHours.help': 'Hours an export file stays downloadable before it is removed (an organisation may set a shorter window).',
  'set.k.pdf.threads.label': 'Parallel PDF renders',
  'set.k.pdf.threads.help': 'PDF exports rendered at once per process.',
  'set.k.pdf.timeoutMs.label': 'PDF render timeout',
  'set.k.pdf.timeoutMs.help': 'A render taking longer fails; nothing partial is kept.',
  'set.k.imports.maxBytes.label': 'Largest import file',
  'set.k.imports.maxBytes.help': 'Largest spreadsheet accepted by the record import and visit rosters.',
  'set.k.imports.maxRows.label': 'Import rows',
  'set.k.imports.maxRows.help': 'Rows accepted per record import.',
  'set.k.imports.maxColumns.label': 'Import columns',
  'set.k.imports.maxColumns.help': 'Columns accepted per import file.',
  'set.k.imports.maxUncompressedBytes.label': 'Import unpacked size',
  'set.k.imports.maxUncompressedBytes.help': 'Largest unpacked size of a spreadsheet (protects against compressed bombs).',
  'set.k.imports.chunkRows.label': 'Import chunk',
  'set.k.imports.chunkRows.help': 'Rows applied per step when only valid rows are imported.',
  'set.k.imports.rosterMaxRows.label': 'Roster rows',
  'set.k.imports.rosterMaxRows.help': 'Rows accepted per visit roster.',
  'set.k.devices.staleSeconds.label': 'Device heartbeat',
  'set.k.devices.staleSeconds.help': 'Seconds without a heartbeat before a device shows as stale.',
  'set.k.edge.commandTtlSeconds.label': 'Edge command lifetime',
  'set.k.edge.commandTtlSeconds.help': 'Seconds a signed edge-agent command stays valid.',
} as const;
